/**
 * Company Detail Page
 *
 * Comprehensive company view with tabs:
 * - Overview: Basic info, status, risk
 * - Beneficial Owners: UBO list with add/edit/link functionality
 * - Documents: Corporate documents
 * - Screening: Screening results and history
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowLeft,
  Users,
  FileText,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Globe,
  Mail,
  Phone,
  Link as LinkIcon,
  Calendar,
  Edit,
  PlayCircle,
  Search,
  X,
  RotateCcw,
  Download,
} from 'lucide-react';
import {
  useCompany,
  useReviewCompany,
  useScreenCompany,
  useUpdateCompany,
  useDownloadCompanyEvidence,
} from '../../hooks';
import CompanyForm from './CompanyForm';
import UBOList from './UBOList';
import UBOVisualization from './UBOVisualization';
import CompanyDocuments from './CompanyDocuments';
import CompanyScreening from './CompanyScreening';
import CompanyRiskNarrative from './CompanyRiskNarrative';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#8b919e', bg: 'rgba(139, 145, 158, 0.1)', icon: Clock },
  in_review: { label: 'In Review', color: 'var(--accent-primary)', bg: 'var(--accent-glow-medium)', icon: Search },
  approved: { label: 'Approved', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', icon: XCircle },
};

const RISK_CONFIG = {
  low: { label: 'Low Risk', color: '#22c55e' },
  medium: { label: 'Medium Risk', color: '#f59e0b' },
  high: { label: 'High Risk', color: '#ef4444' },
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'ubos', label: 'Beneficial Owners', icon: Users },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'screening', label: 'Screening', icon: Shield },
];

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [reviewModal, setReviewModal] = useState({ open: false, decision: null });
  const [reviewNotes, setReviewNotes] = useState('');
  const [riskOverride, setRiskOverride] = useState('');
  const [reviewError, setReviewError] = useState('');

  const { data: company, isLoading, error } = useCompany(id);
  const reviewMutation = useReviewCompany();
  const screenMutation = useScreenCompany();
  const updateMutation = useUpdateCompany();
  const evidenceMutation = useDownloadCompanyEvidence();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        Loading company details...
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="error-container">
        <AlertTriangle size={48} />
        <h2>Company not found</h2>
        <button onClick={() => navigate('/companies')}>Back to Companies</button>
      </div>
    );
  }

  const status = STATUS_CONFIG[company.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const risk = RISK_CONFIG[company.risk_level];

  const openReviewModal = (decision) => {
    setReviewModal({ open: true, decision });
    setReviewNotes('');
    setRiskOverride('');
    setReviewError('');
  };

  const closeReviewModal = () => {
    setReviewModal({ open: false, decision: null });
    setReviewNotes('');
    setRiskOverride('');
    setReviewError('');
  };

  const handleReviewSubmit = async () => {
    if (reviewNotes.trim().length < 10) {
      setReviewError('Review notes must be at least 10 characters');
      return;
    }
    try {
      await reviewMutation.mutateAsync({
        id,
        decision: reviewModal.decision,
        notes: reviewNotes.trim(),
        riskOverride: riskOverride ? parseInt(riskOverride, 10) : undefined,
      });
      closeReviewModal();
    } catch (err) {
      setReviewError(err?.message || 'Failed to submit review');
    }
  };

  const handleScreen = async () => {
    try {
      await screenMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to run screening:', err);
    }
  };

  const handleUpdate = async (data) => {
    try {
      await updateMutation.mutateAsync({ id, data });
      setShowEditModal(false);
    } catch (err) {
      console.error('Failed to update company:', err);
    }
  };

  const isReviewable = company.status === 'pending' || company.status === 'in_review';
  const isReviewed = company.status === 'approved' || company.status === 'rejected';

  return (
    <div className="company-detail">
      <style>{`
        .company-detail {
          max-width: 1200px;
          margin: 0 auto;
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 400px;
          color: var(--text-secondary, #8b919e);
          gap: 16px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary, #8b919e);
          text-decoration: none;
          font-size: 14px;
          margin-bottom: 16px;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          font-family: inherit;
        }

        .back-link:hover {
          color: var(--text-primary, #f0f2f5);
        }

        .back-link svg {
          width: 16px;
          height: 16px;
        }

        .company-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 24px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .company-info {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .company-icon {
          width: 56px;
          height: 56px;
          background: var(--bg-secondary, #111318);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent-primary, var(--accent-primary));
        }

        .company-icon svg {
          width: 28px;
          height: 28px;
        }

        .company-title-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .company-name {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }

        .company-trading {
          font-size: 14px;
          color: var(--text-secondary, #8b919e);
        }

        .company-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }

        .status-badge svg {
          width: 14px;
          height: 14px;
        }

        .risk-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }

        .risk-badge svg {
          width: 14px;
          height: 14px;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        @media (max-width: 1100px) {
          .header-actions .action-btn .action-label {
            display: none;
          }
          .header-actions .action-btn {
            padding: 8px 10px;
          }
        }

        .actions-separator {
          width: 1px;
          height: 24px;
          background: var(--border-primary, #23262f);
          margin: 0 4px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
          border: 1px solid var(--border-primary, #23262f);
          background: var(--bg-secondary, #111318);
          color: var(--text-primary, #f0f2f5);
          height: 36px;
          white-space: nowrap;
        }

        .action-btn:hover {
          background: var(--bg-tertiary, #1a1d24);
        }

        .action-btn svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .action-btn.ghost {
          background: transparent;
          border-color: transparent;
        }

        .action-btn.ghost:hover {
          background: var(--bg-secondary, #111318);
          border-color: var(--border-primary, #23262f);
        }

        .action-btn.primary {
          background: var(--accent-primary, var(--accent-primary));
          border-color: var(--accent-primary, var(--accent-primary));
          color: white;
        }

        .action-btn.primary:hover {
          background: var(--accent-hover, #5558e3);
        }

        .action-btn.success {
          background: rgba(34, 197, 94, 0.1);
          border-color: rgba(34, 197, 94, 0.3);
          color: #22c55e;
        }

        .action-btn.success:hover {
          background: rgba(34, 197, 94, 0.2);
        }

        .action-btn.danger-outline {
          background: transparent;
          border-color: rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }

        .action-btn.danger-outline:hover {
          background: rgba(239, 68, 68, 0.1);
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Review Modal */
        .review-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
          animation: fadeIn 0.15s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .review-modal {
          background: var(--bg-primary, #0a0b0e);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 12px;
          width: 100%;
          max-width: 480px;
          animation: scaleIn 0.15s ease-out;
        }

        .review-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border-primary, #23262f);
        }

        .review-modal-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }

        .review-modal-body {
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .review-field-label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary, #8b919e);
          margin-bottom: 8px;
          display: block;
        }

        .review-field-label .required {
          color: #ef4444;
          margin-left: 2px;
        }

        .review-textarea {
          width: 100%;
          min-height: 100px;
          padding: 12px;
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 8px;
          color: var(--text-primary, #f0f2f5);
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
          transition: border-color 0.15s;
          box-sizing: border-box;
        }

        .review-textarea:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .review-textarea.error {
          border-color: #ef4444;
        }

        .review-char-count {
          font-size: 11px;
          color: var(--text-secondary, #8b919e);
          text-align: right;
          margin-top: 4px;
        }

        .review-risk-input {
          width: 80px;
          padding: 8px 12px;
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 8px;
          color: var(--text-primary, #f0f2f5);
          font-size: 14px;
          font-family: inherit;
        }

        .review-risk-input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .review-warning {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px;
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.2);
          border-radius: 8px;
          font-size: 12px;
          color: #f59e0b;
          line-height: 1.5;
        }

        .review-warning svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .review-error {
          font-size: 12px;
          color: #ef4444;
        }

        .review-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--border-primary, #23262f);
        }

        .review-btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
          border: none;
        }

        .review-btn-cancel {
          background: var(--bg-secondary, #111318);
          color: var(--text-primary, #f0f2f5);
          border: 1px solid var(--border-primary, #23262f);
        }

        .review-btn-cancel:hover {
          background: var(--bg-tertiary, #1a1d24);
        }

        .review-btn-approve {
          background: #22c55e;
          color: white;
        }

        .review-btn-approve:hover {
          background: #16a34a;
        }

        .review-btn-reject {
          background: #ef4444;
          color: white;
        }

        .review-btn-reject:hover {
          background: #dc2626;
        }

        .review-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .tabs {
          display: flex;
          gap: 4px;
          padding: 4px;
          background: var(--bg-secondary, #111318);
          border-radius: 10px;
          margin-bottom: 24px;
          width: fit-content;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: var(--text-secondary, #8b919e);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          font-family: inherit;
        }

        .tab-btn:hover {
          color: var(--text-primary, #f0f2f5);
          background: var(--bg-tertiary, #1a1d24);
        }

        .tab-btn.active {
          background: var(--accent-primary, var(--accent-primary));
          color: white;
        }

        .tab-btn svg {
          width: 16px;
          height: 16px;
        }

        .tab-badge {
          padding: 2px 6px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          font-size: 11px;
        }

        .tab-content {
          background: var(--bg-secondary, #111318);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 12px;
          padding: 24px;
        }

        .overview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .info-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .info-section-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-primary, #23262f);
        }

        .info-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .info-row svg {
          width: 16px;
          height: 16px;
          color: var(--text-secondary, #8b919e);
          flex-shrink: 0;
        }

        .info-label {
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
          min-width: 120px;
        }

        .info-value {
          font-size: 14px;
          color: var(--text-primary, #f0f2f5);
        }

        .info-value a {
          color: var(--accent-primary, var(--accent-primary));
          text-decoration: none;
        }

        .info-value a:hover {
          text-decoration: underline;
        }

        .flag-list {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .flag-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .flag-badge svg {
          width: 12px;
          height: 12px;
        }

        .address-block {
          font-size: 14px;
          color: var(--text-primary, #f0f2f5);
          line-height: 1.6;
        }
      `}</style>

      <button className="back-link" onClick={() => navigate('/companies')}>
        <ArrowLeft />
        Back to Companies
      </button>

      <div className="company-header">
        <div className="company-info">
          <div className="company-icon">
            <Building2 />
          </div>
          <div className="company-title-section">
            <h1 className="company-name">{company.legal_name}</h1>
            {company.trading_name && company.trading_name !== company.legal_name && (
              <span className="company-trading">DBA: {company.trading_name}</span>
            )}
            <div className="company-badges">
              <span
                className="status-badge"
                style={{ color: status.color, background: status.bg }}
              >
                <StatusIcon />
                {status.label}
              </span>
              {risk && (
                <span
                  className="risk-badge"
                  style={{ color: risk.color, background: `${risk.color}15` }}
                >
                  <Shield />
                  {risk.label}
                </span>
              )}
              {company.has_hits && (
                <span
                  className="risk-badge"
                  style={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)' }}
                >
                  <AlertTriangle />
                  Screening Hits
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="header-actions">
          <button className="action-btn ghost" title="Edit company" onClick={() => setShowEditModal(true)}>
            <Edit />
            <span className="action-label">Edit</span>
          </button>
          <button
            className="action-btn ghost"
            title="Download evidence pack"
            onClick={() => evidenceMutation.mutate({ companyId: id, companyName: company.legal_name })}
            disabled={evidenceMutation.isPending}
          >
            <Download />
            <span className="action-label">{evidenceMutation.isPending ? 'Downloading...' : 'Evidence'}</span>
          </button>
          <button
            className="action-btn"
            title="Run screening"
            onClick={handleScreen}
            disabled={screenMutation.isPending}
          >
            <PlayCircle />
            <span className="action-label">{screenMutation.isPending ? 'Screening...' : 'Screen'}</span>
          </button>
          {isReviewable && (
            <>
              <div className="actions-separator" />
              <button
                className="action-btn success"
                title="Approve company"
                onClick={() => openReviewModal('approved')}
                disabled={reviewMutation.isPending}
              >
                <CheckCircle />
                <span className="action-label">Approve</span>
              </button>
              <button
                className="action-btn danger-outline"
                title="Reject company"
                onClick={() => openReviewModal('rejected')}
                disabled={reviewMutation.isPending}
              >
                <XCircle />
                <span className="action-label">Reject</span>
              </button>
            </>
          )}
          {isReviewed && (
            <>
              <div className="actions-separator" />
              <button
                className="action-btn"
                title="Change review decision"
                onClick={() => openReviewModal(company.status === 'approved' ? 'rejected' : 'approved')}
                disabled={reviewMutation.isPending}
              >
                <RotateCcw />
                <span className="action-label">Change Decision</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon />
            {tab.label}
            {tab.id === 'ubos' && company.ubo_count > 0 && (
              <span className="tab-badge">{company.ubo_count}</span>
            )}
            {tab.id === 'documents' && company.documents?.length > 0 && (
              <span className="tab-badge">{company.documents.length}</span>
            )}
            {tab.id === 'screening' && company.has_hits && (
              <span className="tab-badge" style={{ background: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>!</span>
            )}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && (
          <>
            {/* AI Risk Narrative - placed prominently at top */}
            <div style={{ marginBottom: 24 }}>
              <CompanyRiskNarrative companyId={id} />
            </div>
            <div className="overview-grid">
            <div className="info-section">
              <h3 className="info-section-title">Company Information</h3>
              <div className="info-row">
                <Building2 />
                <span className="info-label">Registration #</span>
                <span className="info-value">
                  {company.registration_number || '—'}
                </span>
              </div>
              <div className="info-row">
                <FileText />
                <span className="info-label">Tax ID</span>
                <span className="info-value">{company.tax_id || '—'}</span>
              </div>
              <div className="info-row">
                <Globe />
                <span className="info-label">Country</span>
                <span className="info-value">
                  {company.incorporation_country || '—'}
                </span>
              </div>
              <div className="info-row">
                <Calendar />
                <span className="info-label">Incorporated</span>
                <span className="info-value">
                  {company.incorporation_date || '—'}
                </span>
              </div>
              <div className="info-row">
                <FileText />
                <span className="info-label">Legal Form</span>
                <span className="info-value">{company.legal_form || '—'}</span>
              </div>
              <div className="info-row">
                <Building2 />
                <span className="info-label">Industry</span>
                <span className="info-value">{company.industry || '—'}</span>
              </div>
            </div>

            <div className="info-section">
              <h3 className="info-section-title">Contact</h3>
              <div className="info-row">
                <LinkIcon />
                <span className="info-label">Website</span>
                <span className="info-value">
                  {company.website ? (
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {company.website}
                    </a>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div className="info-row">
                <Mail />
                <span className="info-label">Email</span>
                <span className="info-value">
                  {company.email ? (
                    <a href={`mailto:${company.email}`}>{company.email}</a>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div className="info-row">
                <Phone />
                <span className="info-label">Phone</span>
                <span className="info-value">{company.phone || '—'}</span>
              </div>
            </div>

            {company.registered_address && (
              <div className="info-section">
                <h3 className="info-section-title">Registered Address</h3>
                <div className="address-block">
                  {company.registered_address.street && (
                    <div>{company.registered_address.street}</div>
                  )}
                  <div>
                    {[
                      company.registered_address.city,
                      company.registered_address.state,
                      company.registered_address.postal_code,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                  {company.registered_address.country && (
                    <div>{company.registered_address.country}</div>
                  )}
                </div>
              </div>
            )}

            {company.flags?.length > 0 && (
              <div className="info-section">
                <h3 className="info-section-title">Risk Flags</h3>
                <div className="flag-list">
                  {company.flags.map((flag) => (
                    <span key={flag} className="flag-badge">
                      <AlertTriangle />
                      {flag.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="info-section">
              <h3 className="info-section-title">Business Details</h3>
              <div className="info-row">
                <Users />
                <span className="info-label">Employees</span>
                <span className="info-value">
                  {company.employee_count || '—'}
                </span>
              </div>
              <div className="info-row">
                <FileText />
                <span className="info-label">Revenue</span>
                <span className="info-value">
                  {company.annual_revenue || '—'}
                </span>
              </div>
              {company.description && (
                <div style={{ marginTop: 8 }}>
                  <span
                    className="info-label"
                    style={{ display: 'block', marginBottom: 8 }}
                  >
                    Description
                  </span>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      color: 'var(--text-primary)',
                      lineHeight: 1.6,
                    }}
                  >
                    {company.description}
                  </p>
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {activeTab === 'ubos' && (
          <>
            <UBOVisualization
              ubos={company?.beneficial_owners || []}
              companyName={company?.legal_name || 'Company'}
            />
            <UBOList companyId={id} company={company} />
          </>
        )}

        {activeTab === 'documents' && <CompanyDocuments companyId={id} />}

        {activeTab === 'screening' && <CompanyScreening companyId={id} company={company} />}
      </div>

      {showEditModal && (
        <CompanyForm
          company={company}
          onSubmit={handleUpdate}
          onClose={() => setShowEditModal(false)}
          isSubmitting={updateMutation.isPending}
        />
      )}

      {reviewModal.open && (
        <div className="review-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeReviewModal(); }}>
          <div className="review-modal">
            <div className="review-modal-header">
              <h3 className="review-modal-title">
                {reviewModal.decision === 'approved' ? (
                  <CheckCircle style={{ color: '#22c55e' }} />
                ) : (
                  <XCircle style={{ color: '#ef4444' }} />
                )}
                {reviewModal.decision === 'approved' ? 'Approve' : 'Reject'} Company
              </h3>
              <button className="close-btn" onClick={closeReviewModal} style={{ padding: 8, background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', borderRadius: 6 }}>
                <X size={20} />
              </button>
            </div>

            <div className="review-modal-body">
              <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                Reviewing <strong style={{ color: 'var(--text-primary)' }}>{company.legal_name}</strong>
              </div>

              <div>
                <label className="review-field-label">
                  Review Notes <span className="required">*</span>
                </label>
                <textarea
                  className={`review-textarea ${reviewError && reviewNotes.trim().length < 10 ? 'error' : ''}`}
                  value={reviewNotes}
                  onChange={(e) => { setReviewNotes(e.target.value); setReviewError(''); }}
                  placeholder={reviewModal.decision === 'approved'
                    ? 'e.g., Clean screening results. Verified incorporation documents. UBOs verified via KYC.'
                    : 'e.g., Sanctions hit confirmed. Company matches OFAC SDN entry. Recommend blocking.'}
                />
                <div className="review-char-count">{reviewNotes.length} characters (min 10)</div>
              </div>

              <div>
                <label className="review-field-label">Risk Score Override (optional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    className="review-risk-input"
                    value={riskOverride}
                    onChange={(e) => setRiskOverride(e.target.value)}
                    placeholder="—"
                    min={0}
                    max={100}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>/ 100</span>
                </div>
              </div>

              <div className="review-warning">
                <AlertTriangle />
                <span>This decision will be audit-logged and associated with your account. Review decisions should reflect your compliance assessment.</span>
              </div>

              {reviewError && <div className="review-error">{reviewError}</div>}
            </div>

            <div className="review-modal-footer">
              <button className="review-btn review-btn-cancel" onClick={closeReviewModal}>
                Cancel
              </button>
              <button
                className={`review-btn ${reviewModal.decision === 'approved' ? 'review-btn-approve' : 'review-btn-reject'}`}
                onClick={handleReviewSubmit}
                disabled={reviewMutation.isPending || reviewNotes.trim().length < 10}
              >
                {reviewMutation.isPending ? 'Submitting...' : `Submit ${reviewModal.decision === 'approved' ? 'Approval' : 'Rejection'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
